// ENG-5630 (parent ENG-5626): conversation metric aggregation.
//
// Pure, DB-agnostic, browser-safe aggregation for conversation metrics. Callers
// (the per-org admin tab via the Hono API, and the cross-org platform admin
// Dashboard via the webapp) fetch end-user conversation rows over a window and
// hand them here; keeping the bucketing + distinct-counting pure makes it
// unit-testable without a database and shareable across packages.
//
// Lives in @augmented/core (not @augmented/api) so both the Hono API and the
// Next.js webapp — which only depends on @augmented/core — can use it. ENG-5648
// moved it here from packages/api/src/lib so the platform Dashboard could reuse
// it cross-org rather than duplicate the bucketing.
//
// Why no SQL rollup table: the headline metrics are conversations-started
// (additive, so it time-buckets cleanly into a stacked bar) and unique
// end-users (a COUNT(DISTINCT sender) that CANNOT be summed across buckets).
// We therefore time-bucket only the started count and report unique users as a
// single window total per channel. Conversations are low-volume (one row per
// conversation, not per message), so aggregating the window's rows in-process
// is both correct and cheap. The cross-org caller (ENG-5648) keeps the input
// bounded with .range() pagination so this premise still holds platform-wide.

export type ConversationMetricsPeriod = '24h' | '7d' | '30d';

/** Window length + bucket granularity per period. 24h buckets hourly; multi-day buckets daily. */
const PERIOD_CONFIG: Record<
  ConversationMetricsPeriod,
  { windowMs: number; bucket: 'hour' | 'day' }
> = {
  '24h': { windowMs: 24 * 60 * 60 * 1000, bucket: 'hour' },
  '7d': { windowMs: 7 * 24 * 60 * 60 * 1000, bucket: 'day' },
  '30d': { windowMs: 30 * 24 * 60 * 60 * 1000, bucket: 'day' },
};

export const CONVERSATION_METRICS_PERIODS = Object.keys(PERIOD_CONFIG) as ConversationMetricsPeriod[];

export function isConversationMetricsPeriod(v: unknown): v is ConversationMetricsPeriod {
  return typeof v === 'string' && v in PERIOD_CONFIG;
}

/** One end-user conversation row (already filtered to sender_class='end_user' by the caller). */
export interface ConversationMetricRow {
  channel: string;
  sender_id: string | null;
  started_at: string;
}

export interface ChannelTotals {
  channel: string;
  conversations_started: number;
  unique_end_users: number;
}

/** A time bucket: ISO bucket start + per-channel conversations-started counts. */
export interface ConversationSeriesPoint {
  bucket: string;
  /** channel -> conversations started in this bucket */
  counts: Record<string, number>;
}

export interface ConversationMetricsResult {
  period: ConversationMetricsPeriod;
  bucket: 'hour' | 'day';
  period_start: string;
  channels: string[];
  series: ConversationSeriesPoint[];
  totals: ChannelTotals[];
  overall: { conversations_started: number; unique_end_users: number };
}

/** Truncate a date to the start of its UTC hour or day. */
function truncateUtc(d: Date, bucket: 'hour' | 'day'): Date {
  const t = new Date(d);
  t.setUTCMinutes(0, 0, 0);
  if (bucket === 'day') t.setUTCHours(0);
  return t;
}

/**
 * Identity key for a distinct end-user. sender_id is CHANNEL-SCOPED (a Slack
 * user_id, a Telegram id, a direct-chat auth user) — it is NOT a stable
 * cross-channel person, and two tenants can carry colliding raw ids. Namespacing
 * by channel stops cross-channel/cross-tenant collisions from under-counting; it
 * does mean one human active on two channels counts as two participants, which
 * is why the metric is labelled "participants", not "people". (ENG-5648.)
 */
function participantKey(channel: string, senderId: string): string {
  return `${channel}:${senderId}`;
}

/**
 * Aggregate end-user conversation rows into a per-channel time series
 * (conversations started) plus per-channel + overall window totals (incl.
 * unique end users). `now` is injectable for deterministic tests.
 */
export function aggregateConversationMetrics(
  rows: ConversationMetricRow[],
  period: ConversationMetricsPeriod,
  now: Date = new Date(),
): ConversationMetricsResult {
  const { windowMs, bucket } = PERIOD_CONFIG[period];
  const periodStart = new Date(now.getTime() - windowMs);

  // Pre-build empty buckets so the chart has a continuous x-axis even for
  // quiet periods (no gaps where a bucket had zero conversations).
  const stepMs = bucket === 'hour' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
  const firstBucket = truncateUtc(periodStart, bucket);
  const seriesMap = new Map<string, Record<string, number>>();
  for (let t = firstBucket.getTime(); t <= now.getTime(); t += stepMs) {
    seriesMap.set(new Date(t).toISOString(), {});
  }

  const channels = new Set<string>();
  const perChannelCount = new Map<string, number>();
  const perChannelUsers = new Map<string, Set<string>>();
  const overallUsers = new Set<string>();

  for (const row of rows) {
    const started = new Date(row.started_at);
    if (started < periodStart || started > now) continue;
    channels.add(row.channel);

    const bucketKey = truncateUtc(started, bucket).toISOString();
    const point = seriesMap.get(bucketKey) ?? {};
    point[row.channel] = (point[row.channel] ?? 0) + 1;
    seriesMap.set(bucketKey, point);

    perChannelCount.set(row.channel, (perChannelCount.get(row.channel) ?? 0) + 1);

    if (row.sender_id) {
      let set = perChannelUsers.get(row.channel);
      if (!set) {
        set = new Set<string>();
        perChannelUsers.set(row.channel, set);
      }
      set.add(row.sender_id);
      // Overall distinct is namespaced by channel so a cross-tenant id clash
      // between, say, two Slack workspaces doesn't collapse two people into one.
      overallUsers.add(participantKey(row.channel, row.sender_id));
    }
  }

  const sortedChannels = [...channels].sort();
  const series: ConversationSeriesPoint[] = [...seriesMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucketKey, counts]) => ({ bucket: bucketKey, counts }));

  const totals: ChannelTotals[] = sortedChannels.map((channel) => ({
    channel,
    conversations_started: perChannelCount.get(channel) ?? 0,
    unique_end_users: perChannelUsers.get(channel)?.size ?? 0,
  }));

  return {
    period,
    bucket,
    period_start: periodStart.toISOString(),
    channels: sortedChannels,
    series,
    totals,
    overall: {
      conversations_started: rows.filter((r) => {
        const s = new Date(r.started_at);
        return s >= periodStart && s <= now;
      }).length,
      unique_end_users: overallUsers.size,
    },
  };
}

/** One time bucket of the unique-participants trend. */
export interface UniqueParticipantsPoint {
  bucket: string;
  /** Distinct channel-namespaced participants active in THIS bucket alone. */
  unique_participants: number;
}

export interface UniqueParticipantsResult {
  period: ConversationMetricsPeriod;
  bucket: 'hour' | 'day';
  period_start: string;
  series: UniqueParticipantsPoint[];
  /** Distinct participants across the whole window (NOT the sum of the series). */
  overall_unique: number;
}

/**
 * Daily (or hourly, for 24h) distinct end-user participants.
 *
 * IMPORTANT: each point is an independent COUNT(DISTINCT) for that bucket and is
 * NOT additive — summing the series over-counts anyone active on multiple days.
 * The window total is computed separately as `overall_unique`. The UI must
 * annotate the series as non-additive (ENG-5648, council Skeptic-4). Rows with a
 * null sender_id contribute no participant (no stable identity to dedupe on).
 */
export function aggregateDailyUniqueParticipants(
  rows: ConversationMetricRow[],
  period: ConversationMetricsPeriod,
  now: Date = new Date(),
): UniqueParticipantsResult {
  const { windowMs, bucket } = PERIOD_CONFIG[period];
  const periodStart = new Date(now.getTime() - windowMs);

  const stepMs = bucket === 'hour' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
  const firstBucket = truncateUtc(periodStart, bucket);

  // Pre-build a continuous axis of empty per-bucket distinct-sets.
  const bucketSets = new Map<string, Set<string>>();
  for (let t = firstBucket.getTime(); t <= now.getTime(); t += stepMs) {
    bucketSets.set(new Date(t).toISOString(), new Set<string>());
  }

  const overall = new Set<string>();
  for (const row of rows) {
    if (!row.sender_id) continue;
    const started = new Date(row.started_at);
    if (started < periodStart || started > now) continue;
    const key = participantKey(row.channel, row.sender_id);
    const bucketKey = truncateUtc(started, bucket).toISOString();
    const set = bucketSets.get(bucketKey);
    if (set) set.add(key);
    overall.add(key);
  }

  const series: UniqueParticipantsPoint[] = [...bucketSets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucketKey, set]) => ({ bucket: bucketKey, unique_participants: set.size }));

  return {
    period,
    bucket,
    period_start: periodStart.toISOString(),
    series,
    overall_unique: overall.size,
  };
}
